// Loading a website into the editor, and saving it back.
// The editor keeps one draft in memory. Saving compares the draft with a snapshot of what the server
// had ("base") and sends only the differences, one request per changed item.
import { errorMessage } from "../../services";
import { createCategory, deleteCategory, getCategories, updateCategory } from "../../services/categories";
import { createProduct, deleteProduct, getAllProducts, updateProduct } from "../../services/products";
import { getSite, updateSite } from "../../services/sites";

const CONTENT_FIELDS = ["name", "category", "color", "template", "layout", "logo", "favicon", "announcement", "about", "privacyPolicy", "termsConditions"];

// Store types and their names (the backend's services/starterCatalog/templates.js). The type decides the storefront's
// wording (the store's own products, banners and texts never change with it).
export const STORE_TYPES = {
  plants: "Plants & Gardening",
  pets: "Pet Store",
  aquarium: "Aquarium Store",
  gifts: "Gift Store",
  grocery: "Grocery Store",
  fresh: "Fruits & Vegetables",
  selfcare: "Soaps, Perfumes & Handwash",
  clothing: "Clothing Store",
  tech: "Phone Cases & Tech Accessories",
  footwear: "Footwear & Juttis",
  bags: "Bags & Totes",
  crafts: "Custom Crafts & Resin Art",
  decor: "Home Decor & Candles",
  blouses: "Aari & Designer Blouses",
  uruli: "Brassware & Bronze Urulis",
  favors: "Wedding Return Gifts & Favors",
  resin: "Garland & Floral Resin Art",
  crochet: "Handmade Crochet Gifts",
  pattu: "Kids Ethnic & Pattu Pavadai",
  linens: "Handblock Bedsheets & Dohars",
  tshirts: "Custom Printed T-Shirts",
  acrylic: "Acrylic Photos & Wall Art",
  beauty: "Fashion & Beauty Store",
  cosmetics: "Cosmetics & Makeup Brand",
  kurti: "Customised Kurtis",
  perfume: "Perfumes, Attars & Custom Soaps",
  toys: "Wholesale Toys",
  medcare: "Medical & Healthcare Products",
  dryfruits: "Dry Fruits & Nuts",
  bedbath: "Bedsheets & Towels",
  lungi: "Gamcha, Lungi & Dhoti",
  organic: "Organic Products",
  murti: "Murtis & Sculptures",
  spices: "Spices & Masalas",
  snacks: "Chips, Fries & Namkeen",
  teacoffee: "Tea & Coffee",
  gardentools: "Gardening Tools",
  pickles: "Pickles, Podis & Masalas",
  bakery: "Home Bakery & Custom Cakes",
  jewellery: "Imitation & Temple Jewellery",
  sarees: "Silk & Handloom Sarees",
  sweets: "Homemade Sweets",
  herbal: "Herbal Hair Oils & Skin Care",
  healthmix: "Health Mix & Baby Food",
  dailywear: "Women Daily Wear",
  tiffin: "Home Food, Tiffin & Catering",
  bangles: "Bangles & Bridal Accessories",
  pooja: "Pooja Decor & Brass Items",
  electronics: "Electronics & Gadgets",
  comics: "Comic Books & Manga",
  coconutoil: "Homemade Coconut Oil",
  cleaning: "Bathroom & Toilet Cleaning Products",
  artjewellery: "Artificial & Handmade Jewellery",
  wallclocks: "Acrylic Wall Clocks",
  mirrors: "Acrylic & Wall Mirrors",
  candles: "Scented Candles",
  custom: "Customized Store",
};
// Same limits as the backend (services/featuredProducts.js, services/offerProducts.js)
export const FEATURED_LIMIT = 12;
export const OFFERS_LIMIT = 50;

// What the storefront shows while the owner has not picked featured products: the newest ones
export const newestProducts = (products) =>
  products
    .filter((p) => typeof p.id === "number")
    .slice()
    .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")) || b.id - a.id)
    .slice(0, FEATURED_LIMIT);

const blankBanner = () => ({ _key: `hero_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, title: "", description: "", image: "", mobileImage: "", imageOnly: false });

// The fields the products API accepts, in the right types
export function productPayload(p) {
  return {
    name: String(p.name || "").trim(),
    desc: p.desc || "",
    cat: p.cat,
    price: Number(p.price) || 0,
    originalPrice: Number(p.originalPrice) || 0,
    stock: Math.max(0, Math.round(Number(p.stock) || 0)),
    unitNote: p.unitNote || "per pc",
    availability: p.availability || "In stock",
    img: p.img || "",
    tags: (Array.isArray(p.tags) ? p.tags : []).map((t) => t.trim()).filter(Boolean),
    // Optional text for the product in the Offers section
    offerTitle: String(p.offerTitle || "").trim(),
    offerNote: String(p.offerNote || "").trim(),
    // Clothing stores only: sent once the product has them, so other products are saved as before
    ...(Array.isArray(p.gallery) ? { gallery: p.gallery.filter(Boolean) } : {}),
    ...(Array.isArray(p.sizes) ? { sizes: p.sizes } : {}),
    // Size based price: one row per size (empty rows are left out)
    ...(Array.isArray(p.sizePrices) ? { sizePrices: p.sizePrices.filter((x) => x && String(x.size || "").trim() && x.price !== "" && Number.isFinite(Number(x.price))).map((x) => ({ size: String(x.size).trim(), price: Number(x.price), originalPrice: Number(x.originalPrice) || 0 })) } : {}),
    ...(typeof p.optionLabel === "string" ? { optionLabel: p.optionLabel.trim() } : {}),
    ...(p.personalization ? { personalization: { text: Boolean(p.personalization.text), textLabel: String(p.personalization.textLabel || "").trim(), photos: Number(p.personalization.photos) || 0, required: Boolean(p.personalization.required) } } : {}),
    ...(Array.isArray(p.colors) ? { colors: p.colors.map(({ name, hex }) => ({ name, hex: hex || "" })) } : {}),
  };
}

// Banners that were left completely empty are not saved
const cleanBanners = (banners) =>
  banners.filter((b) => b.title || b.description || b.image).map((b) => ({ title: b.title || "", description: b.description || "", image: b.image || "", mobileImage: b.mobileImage || "", imageOnly: Boolean(b.imageOnly) }));

// The home page's trust points and highlight cards
const cleanCards = (cards) => cards.map((c) => ({ icon: (c.icon || "").trim(), title: (c.title || "").trim(), text: (c.text || "").trim(), hidden: Boolean(c.hidden) }));

function contentOf(draft, isSuper) {
  const content = Object.fromEntries(CONTENT_FIELDS.map((key) => [key, draft[key] ?? ""]));
  content.heroBanners = cleanBanners(draft.heroBanners);
  content.bannerSource = draft.bannerSource === "theme" ? "theme" : "custom";
  // The theme banners as edited; unchanged from the theme's own = [] (the store keeps following the theme)
  const themeBanners = cleanBanners(draft.themeBanners);
  content.themeBanners = JSON.stringify(themeBanners) === JSON.stringify(cleanBanners(draft.themeDefaults)) ? [] : themeBanners;
  content.featuredProducts = draft.featuredProducts || null; // null = automatic (newest products)
  content.offers = { title: (draft.offers.title || "").trim() || "Offers", products: draft.offers.products };
  content.highlights = cleanCards(draft.highlights);
  content.highlightsOff = Boolean(draft.highlightsOff);
  const shopBy = draft.shopBy;
  content.shopBy = {
    off: Boolean(shopBy.off), eyebrow: shopBy.eyebrow.trim(), title: shopBy.title.trim(), subtitle: shopBy.subtitle.trim(), budgetPrefix: shopBy.budgetPrefix.trim(),
    items: shopBy.items.filter((x) => x.name).map((x) => ({ icon: (x.icon || "").trim(), name: x.name })),
    budgets: shopBy.budgets.map(Number).filter((n) => n > 0),
  };
  content.spotlights = cleanCards(draft.spotlights);
  content.seo = { title: (draft.seo.title || "").trim(), description: (draft.seo.description || "").trim(), keywords: draft.seo.keywords.map((k) => k.trim()).filter(Boolean) };
  content.aboutPage = { title: (draft.aboutPage.title || "").trim() || "About us", text: draft.aboutPage.text || "", image: draft.aboutPage.image || "" };
  if (isSuper) content.domain = draft.domain || "";
  return content;
}

// Everything the editor needs, plus the snapshot used later to work out what changed
export async function loadSite(siteId, isSuper) {
  const [{ data: siteData }, { data: categoryData }, products] = await Promise.all([getSite(siteId), getCategories(siteId), getAllProducts(siteId)]);
  const site = siteData.site;

  const draft = {
    name: site.name,
    category: site.category,
    // blank when the store only uses its free <slug>.ownezzy.com address
    domain: site.domain === site.subdomain ? "" : site.domain,
    color: site.color,
    template: site.template || "plants",
    layout: site.layout || "",
    logo: site.logo || "",
    favicon: site.favicon || "",
    announcement: site.announcement,
    about: site.about,
    privacyPolicy: site.privacyPolicy,
    termsConditions: site.termsConditions,
    heroBanners: site.heroBanners.length ? site.heroBanners.map((b) => ({ ...b, _key: b._id })) : [blankBanner()],
    // "theme": the store type's ready-made banners (themeBanners, shown read-only); "custom": the banners below
    bannerSource: site.bannerSource === "theme" ? "theme" : "custom",
    themeBanners: (site.themeBanners || []).map((b, i) => ({ ...b, _key: `theme_${i}_${Date.now()}` })),
    themeDefaults: site.themeDefaults || [],
    featuredProducts: site.featuredProducts || null,
    highlights: site.highlights || [],
    highlightsOff: Boolean(site.highlightsOff),
    // The home page's "Shop by" section (empty = the store kind's defaults; chips = the store's categories)
    shopBy: { off: false, eyebrow: "", title: "", subtitle: "", items: [], budgetPrefix: "", budgets: [], ...(site.shopBy || {}) },
    spotlights: site.spotlights || [],
    offers: { title: (site.offers && site.offers.title) || "Offers", products: (site.offers && site.offers.products) || [] },
    seo: { title: (site.seo && site.seo.title) || "", description: (site.seo && site.seo.description) || "", keywords: (site.seo && site.seo.keywords) || [] },
    seoDefaults: site.seoDefaults || { title: "", description: "", keywords: [] },
    aboutPage: { title: (site.aboutPage && site.aboutPage.title) || "About us", text: (site.aboutPage && site.aboutPage.text) || "", image: (site.aboutPage && site.aboutPage.image) || "" },
    categories: categoryData.categories.map((c) => c.name),
    categoryIds: categoryData.categories.map((c) => c.id),
    inactiveCategories: categoryData.categories.filter((c) => !c.active).map((c) => c.name),
    products,
  };

  const base = {
    content: contentOf(draft, isSuper),
    categories: categoryData.categories.map((c) => ({ id: c.id, name: c.name, active: c.active })),
    products: new Map(products.map((p) => [p.id, p])),
  };
  return { draft, base, meta: { slug: site.id, subdomain: site.subdomain, url: site.url } };
}

// A product the server saved on its own (a combo pack) joins the draft and the snapshot without a reload, so the
// owner's unsaved edits stay. The category the server made for it (Combo packs) and its place in Offers come along.
// Returns the new draft.
export async function mergeSavedProduct(siteId, draft, base, product) {
  const [{ data: categoryData }, { data: siteData }] = await Promise.all([getCategories(siteId), getSite(siteId)]);
  const next = { ...draft, categories: [...draft.categories], categoryIds: [...draft.categoryIds], inactiveCategories: [...draft.inactiveCategories] };
  for (const c of categoryData.categories) {
    if (base.categories.some((known) => known.id === c.id) || next.categoryIds.includes(c.id)) continue;
    base.categories.push({ id: c.id, name: c.name, active: c.active });
    next.categories.push(c.name);
    next.categoryIds.push(c.id);
    if (!c.active) next.inactiveCategories.push(c.name);
  }
  base.products.set(product.id, product);
  next.products = draft.products.some((p) => p.id === product.id) ? draft.products.map((p) => (p.id === product.id ? product : p)) : [...draft.products, product];
  const onOffer = ((siteData.site.offers && siteData.site.offers.products) || []).includes(product.id);
  if (onOffer && !base.content.offers.products.includes(product.id)) base.content.offers = { ...base.content.offers, products: [...base.content.offers.products, product.id] };
  if (onOffer && !draft.offers.products.includes(product.id)) next.offers = { ...draft.offers, products: [...draft.offers.products, product.id] };
  return next;
}

// One product saved straight from its form (Add / Edit product), without the editor's Save changes: the draft and
// the snapshot both take the saved product, so the owner's other unsaved edits stay as they are. The sample products
// the server removes once the owner adds their own leave the draft too. Returns { draft, product, placeholdersRemoved }.
export async function saveProductNow(siteId, draft, base, p) {
  const payload = productPayload(p);
  const isNew = typeof p.id === "string";
  const { data } = isNew ? await createProduct(siteId, payload) : await updateProduct(siteId, p.id, payload);
  const saved = data.product;
  base.products.set(saved.id, saved);
  let products = isNew ? [...draft.products.filter((x) => x.id !== p.id), saved] : draft.products.map((x) => (x.id === p.id ? saved : x));
  const placeholdersRemoved = data.placeholdersRemoved || 0;
  if (placeholdersRemoved) {
    const live = new Set((await getAllProducts(siteId)).map((x) => x.id));
    products = products.filter((x) => typeof x.id === "string" || live.has(x.id));
    [...base.products.keys()].forEach((id) => { if (!live.has(id)) base.products.delete(id); });
  }
  return { draft: { ...draft, products }, product: saved, placeholdersRemoved };
}

// A product deleted straight away; it leaves the draft, the snapshot and the featured and offer lists. Returns the new draft.
export async function deleteProductNow(siteId, draft, base, id) {
  if (typeof id !== "string") {
    try {
      await deleteProduct(siteId, id);
    } catch (err) {
      if (!(err && err.response && err.response.status === 404)) throw err; // already gone counts as deleted
    }
    base.products.delete(id);
    if (Array.isArray(base.content.featuredProducts)) base.content.featuredProducts = base.content.featuredProducts.filter((x) => x !== id);
    base.content.offers = { ...base.content.offers, products: base.content.offers.products.filter((x) => x !== id) };
  }
  return {
    ...draft,
    products: draft.products.filter((p) => p.id !== id),
    featuredProducts: draft.featuredProducts && draft.featuredProducts.filter((x) => x !== id),
    offers: { ...draft.offers, products: draft.offers.products.filter((x) => x !== id) },
  };
}

// Sends the differences. `base` is updated after every request that succeeds, so a retry after a failure
// only repeats what is still missing. Returns the problems found, plus the real ids of new items.
export async function saveSite(siteId, draft, base, isSuper) {
  const errors = [];
  const createdProducts = {}; // temporary id -> saved product
  const createdCategories = {}; // category name -> id
  let placeholdersRemoved = 0; // sample products the server removed once the owner added their own

  // gone: the item was already removed on the server (the sample products go by themselves once the owner adds
  // their own product), so it counts as done
  const attempt = async (label, action, gone = null) => {
    try {
      await action();
    } catch (err) {
      if (gone && err && err.response && err.response.status === 404) gone();
      else errors.push(`${label}: ${errorMessage(err)}`);
    }
  };

  // 1. Website content
  const content = contentOf(draft, isSuper);
  const changed = Object.fromEntries(Object.entries(content).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(base.content[key])));
  if (Object.keys(changed).length) {
    await attempt("Website content", async () => {
      await updateSite(siteId, changed);
      Object.assign(base.content, changed);
    });
  }

  // 2. Categories first: renames (the server renames the category's products too), new ones, active switches
  for (let i = 0; i < draft.categories.length; i += 1) {
    const name = draft.categories[i].trim();
    const id = draft.categoryIds[i];
    const active = !draft.inactiveCategories.includes(draft.categories[i]);
    const known = id && base.categories.find((c) => c.id === id);

    if (known && (known.name !== name || known.active !== active)) {
      await attempt(`Category "${name}"`, async () => {
        await updateCategory(siteId, id, { name, active });
        if (known.name !== name) base.products.forEach((p) => { if (p.cat === known.name) p.cat = name; });
        Object.assign(known, { name, active });
      });
    } else if (!known) {
      await attempt(`Category "${name}"`, async () => {
        const { data } = await createCategory(siteId, { name });
        createdCategories[draft.categories[i]] = data.category.id;
        base.categories.push({ id: data.category.id, name, active: true });
        if (!active) {
          await updateCategory(siteId, data.category.id, { active: false });
          base.categories[base.categories.length - 1].active = false;
        }
      });
    }
  }

  // 3. Products: new and changed ones, then removed ones.
  // Only products that existed before this save can be "removed"; ones created just now still carry a temporary id in the draft.
  const productIdsBefore = [...base.products.keys()];
  for (const p of draft.products) {
    const payload = productPayload(p);
    if (typeof p.id === "string") {
      await attempt(`Product "${payload.name}"`, async () => {
        const { data } = await createProduct(siteId, payload);
        createdProducts[p.id] = data.product;
        base.products.set(data.product.id, data.product);
        placeholdersRemoved += data.placeholdersRemoved || 0;
      });
    } else if (JSON.stringify(payload) !== JSON.stringify(productPayload(base.products.get(p.id) || {}))) {
      await attempt(`Product "${payload.name}"`, async () => {
        const { data } = await updateProduct(siteId, p.id, payload);
        base.products.set(p.id, data.product);
        placeholdersRemoved += data.placeholdersRemoved || 0;
      }, () => base.products.delete(p.id));
    }
  }
  const keptIds = new Set(draft.products.map((p) => p.id));
  for (const id of productIdsBefore) {
    const product = base.products.get(id);
    if (product && !keptIds.has(id)) {
      await attempt(`Removing "${product.name}"`, async () => {
        await deleteProduct(siteId, id);
        base.products.delete(id);
      }, () => base.products.delete(id));
    }
  }

  // 4. Removed categories last (the server refuses while products still use them)
  const keptCategoryIds = new Set([...draft.categoryIds, ...Object.values(createdCategories)]);
  for (const category of [...base.categories]) {
    if (!keptCategoryIds.has(category.id)) {
      await attempt(`Removing category "${category.name}"`, async () => {
        await deleteCategory(siteId, category.id);
        base.categories = base.categories.filter((c) => c.id !== category.id);
      }, () => { base.categories = base.categories.filter((c) => c.id !== category.id); });
    }
  }

  return { errors, createdProducts, createdCategories, placeholdersRemoved };
}
